import { FaMap, FaSatellite, FaMountain } from 'react-icons/fa';
import { TbMapOff as NoMap } from 'react-icons/tb';

/** Unique identifier for the base map layer in OpenLayers */
export const BASE_MAP_LAYER_ID = 'baseMapLayer';

/**
 * Helper to build MapTiler URL with a dynamic API key
 * @param {string} path - MapTiler tile path
 * @param {string|null} apiKey - MapTiler API key
 * @returns {string|null} Full URL or null if key missing
 */
const maptilerUrl = (path, apiKey) =>
	apiKey ? `https://api.maptiler.com/${path}?key=${encodeURIComponent(apiKey)}` : null;

/** Default base map selection on component mount */
export const FALLBACK_MAP = 'standard';

/** Preferred base map to switch to once MapTiler key is available */
export const PREFERRED_MAP = 'satellite';

/** Z-index for base map layer positioning */
export const Z_INDEX = 1;

/**
 * Icon sizes used in the selector UI
 * @property {number} sm - Small icon size (option list)
 */
export const ICON_SIZE = {
	sm: 14,
};

/**
 * Build base map configurations with a dynamic MapTiler API key
 * @param {string|null} maptilerApiKey - MapTiler API key fetched from the backend
 * @returns {Object.<string, BaseMapConfig>}
 *
 * @typedef {Object} BaseMapConfig
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {React.ComponentType|null} icon - Icon component
 * @property {boolean} requiresKey - Whether this map requires a MapTiler API key
 * @property {Object|null} xyz - XYZ source configuration
 */
export const getBaseMaps = maptilerApiKey => ({
	satellite: {
		id: 'satellite',
		name: 'Satellite',
		icon: FaSatellite,
		requiresKey: true,
		xyz: {
			url: maptilerUrl('maps/hybrid-v4/{z}/{x}/{y}.jpg', maptilerApiKey),
			attributions: '© MapTiler © OpenStreetMap contributors',
			tileSize: [512, 512],
			maxZoom: 22,
		},
	},
	terrain: {
		id: 'terrain',
		name: 'Terrain',
		icon: FaMountain,
		requiresKey: true,
		xyz: {
			url: maptilerUrl('maps/outdoor-v2/{z}/{x}/{y}.png', maptilerApiKey),
			attributions: '© MapTiler © OpenStreetMap contributors',
			tileSize: [512, 512],
			maxZoom: 22,
		},
	},
	standard: {
		id: 'standard',
		name: 'Standard',
		icon: FaMap,
		xyz: {
			url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
			attributions: '© OpenStreetMap contributors',
			maxZoom: 20,
		},
	},
	no_map: {
		id: 'no_map',
		name: 'No Map',
		icon: NoMap,
		xyz: null,
	},
});
